import { AUTH_CODE_LENGTH } from "@listou/shared/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { AuthLayout } from "../components/auth-layout";
import { CodeInput } from "../components/code-input";
import { Divider } from "../components/divider";
import { authApi } from "../lib/api";
import {
  authErrorMessage,
  readSentAt,
  RESEND_COOLDOWN_SECONDS,
  useCountdown,
  useExpired,
  writeSentAt,
} from "../lib/auth-ui";

/**
 * Step two of sign-in: type the code from the email.
 *
 * A route of its own rather than a state inside /login, so the back button works, a
 * refresh lands back here, and the address is in the URL rather than something the page
 * has to remember.
 */
export const Route = createFileRoute("/login_/verify")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { email: string; redirectTo?: string | undefined } => ({
    email: typeof search.email === "string" ? search.email : "",
    redirectTo: typeof search.redirectTo === "string" ? search.redirectTo : undefined,
  }),
  // Without an address there is nothing to verify against — someone opened this URL by
  // hand or edited it. Send them back to name one rather than rendering a dead form.
  beforeLoad: ({ search }) => {
    if (!search.email) throw redirect({ to: "/login" });
  },
  component: VerifyPage,
});

function VerifyPage() {
  const { email, redirectTo } = useSearch({ from: "/login_/verify" });
  const [code, setCode] = useState("");

  // Falls back to now when storage has no record: a visitor arriving on this URL fresh
  // waits out one cooldown rather than being handed an immediate resend.
  const [sentAt, setSentAt] = useState(() => readSentAt(email) ?? Date.now());

  const config = useQuery({ queryKey: ["auth", "config"], queryFn: authApi.config });
  const cooldown = useCountdown(sentAt, RESEND_COOLDOWN_SECONDS);
  const expired = useExpired(sentAt, config.data?.magicLinkTtlMinutes);

  const verify = useMutation({
    mutationFn: () => authApi.verifyAuthCode({ email, code, redirectTo }),
    onSuccess: (result) => {
      writeSentAt(email, null);
      // A full reload rather than a client navigation: the session cookie was just set,
      // and every cached query on this page was answered as a signed-out visitor.
      window.location.href = result.redirectTo;
    },
  });

  const resend = useMutation({
    mutationFn: () => authApi.requestMagicLink({ email, redirectTo }),
    // Restarts the countdown. The server also limits requests per address, so this is a
    // courtesy to the inbox rather than the actual protection.
    onSuccess: () => {
      const at = Date.now();
      writeSentAt(email, at);
      setSentAt(at);
      setCode("");
    },
  });

  // CodeInput filters to digits on the way in, so what is held here is exactly what gets sent.
  const complete = code.length === AUTH_CODE_LENGTH;

  return (
    <AuthLayout>
      <header className="grid gap-2">
        <h1 className="font-elegant text-6xl leading-[1.02] font-medium">Regardez vos e-mails</h1>
        <p className="text-lg text-muted">
          Un lien et un code viennent de partir vers <strong className="text-ink">{email}</strong>.
          Cliquez sur le lien, ou tapez le code ci-dessous.
        </p>
      </header>

      <form
        className="grid gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (complete) verify.mutate();
        }}
      >
        <CodeInput
          value={code}
          onChange={setCode}
          onComplete={() => verify.mutate()}
          invalid={verify.isError}
          disabled={verify.isPending || expired}
        />
        <button
          className="btn w-full"
          type="submit"
          disabled={!complete || verify.isPending || expired}
        >
          {verify.isPending ? "Connexion…" : "Se connecter"}
        </button>
      </form>

      {expired ? (
        // The server expires the grant and the cleanup job deletes the row, so there is
        // nothing left for this code to match. Say so rather than letting someone type it
        // and read a generic rejection.
        <p className="text-sm text-muted">Ce code a expiré. Demandez-en un nouveau.</p>
      ) : (
        verify.isError && (
          <p className="text-sm text-danger">
            {authErrorMessage(verify.error, "Ce code n'a pas pu être vérifié.")}
          </p>
        )
      )}

      {resend.isError && (
        <p className="text-sm text-danger">
          {authErrorMessage(resend.error, "Le nouvel email n'a pas pu être envoyé.")}
        </p>
      )}

      <Divider />

      <div className="grid gap-2">
        <button
          className="btn-secondary w-full"
          type="button"
          disabled={cooldown > 0 || resend.isPending}
          onClick={() => resend.mutate()}
        >
          {resend.isPending
            ? "Envoi…"
            : cooldown > 0
              ? `Renvoyer dans ${cooldown} s`
              : "Renvoyer l'email"}
        </button>
        <Link to="/login" search={{ redirectTo }} className="btn-secondary w-full">
          Utiliser une autre adresse
        </Link>
      </div>
    </AuthLayout>
  );
}
